/**
 * Local mode RoomProxy implementation.
 *
 * This is a thin wrapper around packages/room's makeRoom,
 * using browser-compatible storage and broadcast adapters.
 */

import { makeRoom } from '@merels/room/room'
import type { Room, RoomCaller } from '@merels/room/room.types'
import type { BroadcastChannel } from '@merels/room/infrastructure.types'
import type { RoomProxy, RoomBroadcast } from './roomProxy.types'
import type { BroadcastListener, GameCatalogClient } from '@merels/room'
import { BrowserStorage } from '../adapters/browserStorage'


/**
 * Browser broadcast channel that also exposes subscription for RoomProxy.
 *
 * `latencyMs` simulates one-way network latency on delivery — real browser
 * local mode never sets this (delivery is synchronous, matching how an
 * in-process room has no real network to cross); only headless BDD
 * scenarios that need to reproduce timing-sensitive networked-play bugs
 * (see setHeadlessBroadcastLatencyMs below) configure it.
 */
class LocalBroadcastChannel implements BroadcastChannel {
  private listeners = new Set<BroadcastListener>()

  constructor(private latencyMs = 0) {}

  broadcast(message: RoomBroadcast): void {
    const deliver = () => {
      this.listeners.forEach(listener => {
        try {
          listener(message)
        } catch (error) {
          console.error('[LocalBroadcastChannel] Error in listener:', error)
        }
      })
    }
    if (this.latencyMs > 0) {
      setTimeout(deliver, this.latencyMs)
    } else {
      deliver()
    }
  }

  send(message: string): void {
    // Not used in local mode
    this.broadcast({ type: 'roomState', data: JSON.parse(message) })
  }

  /** Subscribe to broadcasts (used by Room.subscribe) */
  subscribe(listener: BroadcastListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** @deprecated Use subscribe instead */
  onMessage(listener: BroadcastListener): () => void {
    return this.subscribe(listener)
  }
}

/**
 * Create a local mode RoomProxy.
 *
 * This creates an in-browser Room instance with:
 * - BrowserStorage for state persistence
 * - LocalBroadcastChannel for event subscription
 * - Published-game catalog resolution
 */
/**
 * Create a local mode RoomProxy.
 *
 * This creates an in-browser Room instance with:
 * - BrowserStorage for state persistence
 * - LocalBroadcastChannel for event subscription
 * - Published-game catalog resolution
 */

type SharedLocalRoom = {
  refCount: number
  storage: BrowserStorage
  broadcastChannel: LocalBroadcastChannel
  room: Room
}

// In Node (headless) we want multiple RoomDeps in the same process to be able to
// observe the same local room state. We only share in-memory rooms when `window`
// is not available to avoid changing browser semantics.
const sharedRoomsByName = new Map<string, SharedLocalRoom>()

// Test-only: headless BDD scenarios simulating realistic one-way network
// latency between clients (see spacewar.steps.ts) set this immediately
// before their first client loads the room, so the room this creates uses
// it. Only takes effect for a room actually created afresh — a room
// already shared (refCount > 0) keeps whatever latency it was created
// with. Never touched by real browser local-mode play.
let headlessBroadcastLatencyMs = 0

export function setHeadlessBroadcastLatencyMs(ms: number): void {
  headlessBroadcastLatencyMs = ms
}

function acquireSharedLocalRoom(roomName: string, gameCatalog: GameCatalogClient): SharedLocalRoom {
  const existing = sharedRoomsByName.get(roomName)
  if (existing) {
    existing.refCount++
    return existing
  }

  const storage = new BrowserStorage()
  const broadcastChannel = new LocalBroadcastChannel(headlessBroadcastLatencyMs)
  const room: Room = makeRoom({ storage, broadcastChannel, gameCatalog })

  const created: SharedLocalRoom = { refCount: 1, storage, broadcastChannel, room }
  sharedRoomsByName.set(roomName, created)
  return created
}

function releaseSharedLocalRoom(roomName: string): void {
  const existing = sharedRoomsByName.get(roomName)
  if (!existing) return
  existing.refCount--
  if (existing.refCount <= 0) {
    sharedRoomsByName.delete(roomName)
  }
}

/**
 * Create a local mode RoomProxy.
 *
 * This creates an in-browser Room instance with:
 * - BrowserStorage for state persistence
 * - LocalBroadcastChannel for event subscription
 * - Published-game catalog resolution
 */
export function createLocalRoomProxy(_roomName: string, gameCatalog: GameCatalogClient, clientId?: string): RoomProxy {
  const roomName = _roomName
  // Browser-local rooms are single-owner test rooms with no accounts, so the
  // local client acts as the owner for hosting and lifecycle authority.
  const caller: RoomCaller = { clientId, isOwner: true }

  // Headless view-model tests (apps/web/tests/viewModel) fake a `window`
  // global so other deps that assume a browser don't choke, which defeats
  // this check as originally written — so they also stamp that fake window
  // with `__merelsHeadlessTestShim` to opt back into sharing (see
  // testContainerShims.ts). A real browser tab never has this marker.
  const isHeadless = typeof window === 'undefined' || (window as any).__merelsHeadlessTestShim === true
  const shared = isHeadless ? acquireSharedLocalRoom(roomName, gameCatalog) : null
  const room: Room =
    shared?.room ?? makeRoom({ storage: new BrowserStorage(), broadcastChannel: new LocalBroadcastChannel(), gameCatalog })
  let isDestroyed = false

  const checkDestroyed = () => {
    if (isDestroyed) {
      throw new Error('RoomProxy has been destroyed')
    }
  }

  return {
    destroyProxy: () => {
      if (isDestroyed) return
      isDestroyed = true
      if (shared) {
        releaseSharedLocalRoom(roomName)
      }
    },

    // Delegate all Room methods
    startGame: (args) => { checkDestroyed(); return room.startGame(args, caller) },
    endGame: (args) => { checkDestroyed(); return room.endGame(args, caller) },
    getRoomState: (...args) => { checkDestroyed(); return room.getRoomState(...args) },
    updateMates: (args) => { checkDestroyed(); return room.updateMates(args, caller) },
    flushMates: (...args) => { checkDestroyed(); return room.flushMates(...args) },
    getThumbnailHistory: (...args) => { checkDestroyed(); return room.getThumbnailHistory(...args) },
    deleteThumbnailFromHistory: (...args) => { checkDestroyed(); return room.deleteThumbnailFromHistory(...args) },
    makeMove: (args) => { checkDestroyed(); return room.makeMove(args, caller) },
    forceGameState: (...args) => { checkDestroyed(); return room.forceGameState(...args) },
    // Server-internal in production (see RoomDO's gameCatalog.subscribe push
    // path) — exposed here only for RoomProxy/Room interface parity; local
    // mode has no separate catalog-push wiring yet, so nothing calls this today.
    updateGameVersion: (...args) => { checkDestroyed(); return room.updateGameVersion(...args) },
    setActiveGameVersion: (...args) => { checkDestroyed(); return room.setActiveGameVersion(...args) },
    openGame: (args) => { checkDestroyed(); return room.openGame(args, caller) },
    choosePlayerCount: (args) => { checkDestroyed(); return room.choosePlayerCount(args, caller) },
    updateMateAssignment: (...args) => { checkDestroyed(); return room.updateMateAssignment(...args) },
    startGameFromSetup: (args) => { checkDestroyed(); return room.startGameFromSetup(args, caller) },
    cancelGameSetup: (args) => { checkDestroyed(); return room.cancelGameSetup(args, caller) },
    getGameSetup: (...args) => { checkDestroyed(); return room.getGameSetup(...args) },
    getSavedGameOptions: (...args) => { checkDestroyed(); return room.getSavedGameOptions(...args) },
    setSavedGameOptions: (...args) => { checkDestroyed(); return room.setSavedGameOptions(...args) },
    restartGame: (args) => { checkDestroyed(); return room.restartGame(args, caller) },
    getGameChannelPreference: (...args) => { checkDestroyed(); return room.getGameChannelPreference(...args) },
    setGameChannelPreference: (...args) => { checkDestroyed(); return room.setGameChannelPreference(...args) },
    getChallengeConfigurationPreference: (...args) => { checkDestroyed(); return room.getChallengeConfigurationPreference(...args) },
    setChallengeConfigurationPreference: (...args) => { checkDestroyed(); return room.setChallengeConfigurationPreference(...args) },
    setGamelessMode: (...args) => { checkDestroyed(); return room.setGamelessMode(...args) },
    setHost: (args) => { checkDestroyed(); return room.setHost(args, caller) },
    setManagedMode: (args) => { checkDestroyed(); return room.setManagedMode(args, caller) },
    setHostTerm: (args) => { checkDestroyed(); return room.setHostTerm(args, caller) },
    setCuratedMode: (args) => { checkDestroyed(); return room.setCuratedMode(args, caller) },
    setCuratedGames: (args) => { checkDestroyed(); return room.setCuratedGames(args, caller) },

    // Room connection/lifecycle methods
    onMessage: (...args) => { checkDestroyed(); return room.onMessage(...args) },
  }
}
